import { Widget } from '../widget/widget';
import { WidgetIndex } from '../widget/widgetIndex';
import { ApiResponse } from '../api/api';
import { ApiClient, ResourceApi } from '../api/apiclientfactory';
import { WidgetError } from '../widgeterror';
import { WidgetComponent } from '../widget/widgetComponent';

export class Form {
    private readonly resource: string;

    protected readonly langs: Array<string>;

    protected api: ApiClient;

    private resourceApi: ResourceApi;

    private widgets: Record<string, Widget>;

    protected widgetIndex: WidgetIndex;

    /**
     * @param resource name of the resource
     * @param langs array of present languages
     * @param apiClient instance of an ApiClient
     * @param widgets available widgets for this form
     * @param args additional args to be merged with this
     */
    constructor(
        resource: string,
        langs: Array<string>,
        apiClient: ApiClient,
        widgets: Record<string, Widget>,
        args?: Record<string, unknown>,
    ) {
        this.resource = resource;
        this.langs = langs;
        this.api = apiClient;
        this.resourceApi = this.api.resource(this.resource);
        this.widgets = widgets;
        this.widgetIndex = new WidgetIndex();

        if (args !== null) Object.assign(this, args);

        this.initWidgets();
        this.widgetIndex.observe();
    }

    /**
     * @return {{apiUrl: (*|null)}}
     */
    getDefaultWidgetArgs(): any {
        return {
            // @ts-ignore
            apiUrl: window.baseUrl || null,
            form: this,
        };
    }

    /**
     * @param controls
     */
    filter(controls: NodeList): HTMLElement[] {
        return Array.from(controls).filter((control) => (control.parentNode as HTMLElement).classList.contains('is-widget-group-child') === false
                && (control as HTMLElement).classList.contains('repeater-widget') === false
                && typeof (control as HTMLElement).dataset.manytomany === 'undefined') as HTMLElement[];
    }

    /**
     * creates the widgets, sets the language and adds them to the widgetIndex
     */
    initWidgets(formNode?: Element): void {
        const widgetsUnfiltered = typeof formNode !== 'undefined'
            ? formNode.querySelectorAll('div[data-widget]')
            : document.querySelectorAll('div[data-widget]')
        const widgets: HTMLElement[] = this.filter(widgetsUnfiltered);

        widgets.forEach((node) => {
            const data = node.dataset;
            const args = this.getDefaultWidgetArgs();

            // add language to multilingual widgets
            if (typeof data.widgetLang !== 'undefined' && data.widgetLang !== '') {
                args.lang = data.widgetLang;
            }

            // if widgetname has backslash, assume string is a class with namespace
            // and therefore take last segment of namespace
            const WidgetClass: Widget = (data.widget.indexOf('\\') !== -1)
                ? this.widgets[data.widget.substr(data.widget.lastIndexOf('\\') + 1)]
                : this.widgets[data.widget];

            // do not ignore widgetComponents
            if (data.widget === 'component') {
                const widgetInstance = node.querySelector('[data-component]') as WidgetComponent;
                if (widgetInstance !== null) {
                    this.widgetIndex.addWidget(widgetInstance);
                } else {
                    console.log(`${data.widgetName} should have an element containing an [data-component] attribute.`);
                }
            }

            // create widget instance
            else if (typeof WidgetClass !== 'undefined') {
                // @ts-ignore
                const widgetInstance = new WidgetClass(node, args);
                this.widgetIndex.addWidget(widgetInstance);
            } else {
                console.log(`${data.widgetName} (${data.widget}) has no valid javascript class and thus won’t get added to the WidgetIndex`, WidgetClass);
            }
        });
    }

    /**
     *
     * @return {Promise<boolean>}
     */
    async validate(): Promise<boolean> {
        return this.widgetIndex.validate();
    }

    /**
     *
     * @return {Promise<boolean>}
     */
    async preUploadTasks(): Promise<boolean> {
        return this.widgetIndex.runPreUploadTasks();
    }

    /**
     * gets all non-multilingual widgets
     * @return {Promise<Map>}
     */
    async getValues(): Promise<Map<string, any>> {
        const allWidgets = this.widgetIndex.widgets;

        // only use widgets that are not multilingual
        const widgets = allWidgets.filter((w: Widget) => w.lang == '');
        return this.widgetIndex.getValues(widgets);
    }

    /**
     * gets all multilingual widgets
     * @param lang
     * @return {Promise<Map>}
     */
    async getMultiLangValues(lang: string): Promise<Map<string, any>> {
        const allWidgets = this.widgetIndex.widgets;

        // only use widgets that are of the correct language
        const widgets = allWidgets.filter((w: Widget) => w.lang == lang);
        return this.widgetIndex.getValues(widgets);
    }

    /**
     * validates widgets and posts the values to the api
     * @return {Promise<WidgetError|boolean|ApiResponse>}
     */
    async send(): Promise<WidgetError|boolean|ApiResponse> {
        if (await this.validate() === false) {
            return false;
        }

        if (await this.preUploadTasks() === false) {
            return false;
        }

        const values = await this.getValues();
        let modelPromise: Promise<ApiResponse>;
        if (values.get('id') !== null) {
            modelPromise = this.resourceApi.patch(Object.fromEntries(values.entries()));
        } else {
            modelPromise = this.resourceApi.post(Object.fromEntries(values.entries()));
        }

        return modelPromise.then(async (modelApiResponse) => {
            const resourceId = (values.has('id') && values.get('id') !== null)
                ? values.get('id') // @ts-ignore
                : modelApiResponse.data.id;

            // insert languages
            for (const lang of this.langs) {
                const langData = await this.getMultiLangValues(lang);
                langData.set('id', resourceId);
                await this.resourceApi.patchLanguage(
                    lang,
                    Object.fromEntries(langData.entries()),
                );
            }

            // trigger onSave event
            const widgets = document.querySelectorAll('[data-needs-resource-id]');
            const eventPromises: Array<Promise<void>> = [];
            for (const widget of widgets) {
                eventPromises.push(new Promise((resolve) => {
                    const event = new CustomEvent('resource-saved', { detail: { resourceId, resolve } });
                    widget.dispatchEvent(event);
                }));
            }

            await Promise.all(eventPromises);

            return true;
        }).catch(this.widgetErrorCatcher.bind(this));
    }

    /**
     * annotate widgets with validation error from backend
     * @param error
     */
    widgetErrorCatcher(error: WidgetError): WidgetError {
        // refresh each widget if implemented
        this.widgetIndex.widgets.map(async (w) => w.refresh());

        // reject error if no additional errors defined
        if (typeof error.errors === 'undefined') {
            return error;
        }

        error.errors.forEach((e: any) => {
            if (e.errors.length > 0) {
                const widget = this.widgetIndex.findWidget(e.widget, e.lang);
                if (widget === false) {
                    console.log('WidgetIndex could not find ', e.widget, e);
                } else {
                    try {
                        widget.displayError(e.errors.join(', '));
                    } catch (ex) {} // catch WidgetException
                }
            }
        });

        return error;
    }
}
